import React, { useState, useEffect } from 'react';
import { Grid, Container, CircularProgress } from '@material-ui/core';
import {
  BoxV2 as Box,
  Flex,
  Table,
  FilterType,
  TableFilter,
  useTable,
  useFilters,
} from 'portal-commons';

import { globalGetService } from '../../../../utils/globalApiServices';
import {
  BulkActions,
  CampaignBulkRejectModal,
  ReviewingCampaignListingRow,
} from '../components';
import { toastFlashMessage } from '../../../../utils';
import { Loader, FilterToggleButton } from '../../../../shared_elements';
import { enumController, profileController } from '@/apis/core';
import {
  updateBulkCampaignStatusApi,
  fetchBrandSuggestions,
  fetchCspSuggestions,
} from '../apiServices';
import '../assets/campaign-listing-module.scss';
import { MNO_STATUS } from '../../../../constants';
import { MaxCampaignCount } from '../constants';
import { CampaignsData } from '../types';
import { MnoStatus } from '@/constants/enum';

const FILTER_CONFIG = {
  uid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter Campaign ID',
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
  },
  cspName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Enter CSP DBA Name',
    suggestions: [],
  },
  dcaUid: {
    type: FilterType.Dropdown,
    label: 'DCA Name',
    placeholder: 'Select DCA Name',
    options: [],
  },
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use Case',
    placeholder: 'Use-Case',
  },
  resellerUid: {
    type: FilterType.Text,
    label: 'Reseller ID',
    placeholder: 'Enter Reseller ID',
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    label: 'TCR Status',
    placeholder: 'TCR Status',
    disabled: true,
  },
  mnoStatus: {
    type: FilterType.Dropdown,
    options: MNO_STATUS,
    label: 'MNO Status',
    placeholder: 'MNO Status',
    multiple: true,
    width: 250,
    disabled: true,
  },
};

const TABLE_COLUMNS = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'brandName', label: 'BRAND DBA NAME', sortable: false },
  { id: 'cspName', label: 'CSP DBA NAME', sortable: true },
  { id: 'mnoDcaName', label: 'DCA NAME', sortable: false },
  { id: 'usecase', label: 'USE-CASE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'resellerUid', label: 'RESELLER ID', sortable: false },
  { id: 'tcrStatus', label: 'TCR STATUS', sortable: false },
  { id: 'operationStatus', label: 'MNO STATUS', sortable: false },
];

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => fetchBrandSuggestions({ prefix: value }),
    },
    cspName: {
      minLength: 2,
      load: (value: string) => fetchCspSuggestions({ prefix: value }),
    },
  },
};

function AwaitingReviewedCampaigns() {
  const filter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });
  const { appliedFilters, candidateFilters, additionalFilters } = filter;
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [bulkActionLoader, setBulkActionLoader] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const fetchCampaigns = async (
    tableFilter: any
  ): Promise<{
    data: CampaignsData[];
    rowsPerPage: number;
    total: number;
    page: number;
  }> => {
    const query = {
      mnoStatus: 'REVIEW',
      status: 'ACTIVE',
      ...tableFilter,
      ...appliedFilters,
      ...additionalFilters,
    };

    if (!isActiveAwaitingReview(query)) {
      return {
        data: table.tableData,
        total: table.total,
        rowsPerPage: table.pagination.rowsPerPage,
        page: table.pagination.page,
      };
    }

    const response = await globalGetService('mno/campaigns', query).then(
      (response) => response?.data
    );

    return {
      data: response?.records || [],
      rowsPerPage: response?.recordsPerPage || 10,
      total: response?.totalRecords || 0,
      page: response?.page || 1,
    };
  };

  const table = useTable<CampaignsData>(fetchCampaigns, {
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'createDate',
      ascendingOrder: false,
    },
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    processFilterSpecialCase(appliedFilters, filter.handleApply);
  }, [appliedFilters.status, appliedFilters.mnoStatus]);

  useEffect(() => {
    processFilterSpecialCase(candidateFilters, filter.handleEdit);
  }, [candidateFilters.status, candidateFilters.mnoStatus]);

  const fetchFilterOptions = async () => {
    const { setOptions } = filter;
    const usecaseTypes = await enumController.getUsecaseTypes({
      channelId: '10DLC',
    });
    setOptions(
      'usecase',
      usecaseTypes.map((item: { displayName: string; id: string }) => ({
        label: item.displayName,
        value: item.id,
      }))
    );
    const approvedDcas = await profileController.getApprovedDcas();
    if (approvedDcas) {
      setOptions(
        'dcaUid',
        approvedDcas.map((item) => ({
          label: item.displayName,
          value: item.uid,
        }))
      );
    }
  };

  const isActiveAwaitingReview = (
    filters: Record<string, string | number | undefined>
  ) => {
    const { status, mnoStatus } = filters;
    return status === 'ACTIVE' && mnoStatus === 'REVIEW';
  };

  const processFilterSpecialCase = (
    filters: Record<string, any>,
    callback: (filters: Record<string, any>) => void
  ) => {
    if (!isActiveAwaitingReview(filters)) {
      callback({
        ...filters,
        mnoStatus: 'REVIEW',
        status: 'ACTIVE',
      });
    }
  };

  const handleAppliedFiltersChange = (values = {}) => {
    filter.handleApply({
      ...values,
      status: 'ACTIVE',
      mnoStatus: 'REVIEW',
    });
  };

  const handleSelectCampaign = (selected: string[]) => {
    setSelectedCampaigns(selected);
  };

  const handleUpdateStatus = async (status: string, explanation = '') => {
    setBulkActionLoader(true);
    const formData = {
      explanation:
        status === MnoStatus.Registered
          ? 'Campaign approved'
          : explanation
          ? explanation
          : 'Campaign rejected',
      campaignUid: selectedCampaigns,
      status,
    };
    const result = await updateBulkCampaignStatusApi(formData);
    if (result) {
      setTimeout(() => {
        setBulkActionLoader(false);
        setSelectedCampaigns([]);
        setOpenRejectModal(false);
        toastFlashMessage(
          'Updated the campaign status successfully',
          'success'
        );
        table.reload();
      }, 3000);
    }
  };

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <section className="campaigns-listing-section page-content">
        {table.pageLoading ? (
          <Loader />
        ) : (
          <Container maxWidth={false} className="campaigns-listing-container">
            <Box>
              <div className="title-block">
                <h2>
                  {table.total === 0 || table.pageLoading
                    ? '10DLC Campaigns'
                    : `${table.total} 10DLC Campaigns Awaiting Review`}
                </h2>
                <span>
                  Results capped at first{' '}
                  {MaxCampaignCount.toLocaleString('en-US')} records
                </span>
              </div>
              <BulkActions
                handleUpdateStatus={handleUpdateStatus}
                selectedCampaigns={selectedCampaigns}
                loader={bulkActionLoader}
                handleOpenRejectModal={(flag: boolean) =>
                  setOpenRejectModal(flag)
                }
              />
            </Box>
            <Flex
              sx={{
                flexDirection: 'column',
                marginTop: 's',
                marginBottom: 's',
              }}
            >
              <Box>
                <FilterToggleButton
                  visible={isFilterVisible}
                  onClick={toggleFilterVisibility}
                />
              </Box>
              {isFilterVisible && (
                <TableFilter
                  portal="mno"
                  configs={filter.configs}
                  candidateValues={candidateFilters}
                  appliedValues={appliedFilters}
                  onCandidateValuesChange={filter.handleEdit}
                  onAppliedValuesChange={handleAppliedFiltersChange}
                  className="filters"
                  data-testid="reviewCampaigns"
                />
              )}
            </Flex>
            <Grid
              container
              justifyContent="center"
              spacing={0}
              className="reviewPage"
            >
              {bulkActionLoader ? (
                <div className="loader-wrapper">
                  <CircularProgress size={40} />
                </div>
              ) : (
                <Table
                  testId="campaignsTable"
                  loading={table.loading}
                  handleChangePage={table.handleChangePage}
                  createSortHandler={table.handleSorting}
                  emptyState="no campaigns to view"
                  filter={table.filter}
                  headRows={TABLE_COLUMNS}
                  data={table.tableData as any}
                  rowKey="uid"
                  renderRow={(data, attributes) => (
                    <ReviewingCampaignListingRow
                      key={data.uid as string}
                      campaign={data}
                      selectable={attributes?.selectable}
                      selected={attributes?.selected}
                      onSelectedChange={attributes?.onSelectedChange}
                    />
                  )}
                  pagination={table.pagination}
                  selectable={true}
                  selectedRows={selectedCampaigns}
                  onSelectChange={handleSelectCampaign}
                />
              )}
            </Grid>
          </Container>
        )}
      </section>
      {openRejectModal && !bulkActionLoader && (
        <CampaignBulkRejectModal
          toggleModal={(flag: boolean) => setOpenRejectModal(flag)}
          handleUpdateStatus={handleUpdateStatus}
          loader={bulkActionLoader}
          count={selectedCampaigns.length}
        />
      )}
    </Box>
  );
}
export default AwaitingReviewedCampaigns;
